import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  ArrowLeft, 
  Package, 
  TrendingUp, 
  ShoppingCart, 
  DollarSign, 
  Clock, 
  Save,
  Users,
  Percent,
  BarChart3,
  Calendar,
  Tag
} from "lucide-react";
import { toast } from "sonner";
import { format, startOfDay, endOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { logCrud } from "@/services/activities";
import { useTenant } from "@/contexts/TenantContext";

type Product = {
  id: string;
  nome: string;
  codigo_sku: string | null;
  descricao: string | null;
  preco_venda: number;
  preco_custo: number | null;
  status: string;
  foto_url: string | null;
  unit_measurement_purchase_id: string | null;
  unit_measurement_sale_id: string | null;
  unit_measurement_conversions: Record<string, Record<string, number>> | null;
  created_at: string;
  updated_at: string;
};

type TransactionItem = {
  id: string;
  quantidade: number;
  preco_unitario: number;
  total: number;
  created_at: string;
  transaction_id: string;
  transactions: {
    id: string;
    tipo: string;
    valor_total: number;
    valor_liquido: number;
    status: string;
    created_at: string;
    client_id: string | null;
    clients: {
      id: string;
      nome: string;
    } | null;
  };
};

type ProductStats = {
  totalRevenue: number;
  totalQuantity: number;
  totalTransactions: number;
  averagePrice: number;
  profitMargin: number;
  totalProfit: number;
  lastSale: string | null;
  topClients: { name: string; quantity: number; revenue: number }[];
  monthlySales: { month: string; revenue: number; quantity: number }[];
  paymentMethods: { method: string; value: number }[];
  priceHistory: { date: string; price: number }[];
};

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", { 
    style: "currency", 
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

export default function ProductDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const [isEditing, setIsEditing] = useState(false);
  const search = new URLSearchParams(location.search);
  const isEmbedded = search.get("embedded") === "1";
  const fromStr = search.get("from");
  const toStr = search.get("to");
  const [cadFrom, setCadFrom] = useState<Date>(fromStr ? startOfDay(new Date(fromStr)) : startOfDay(new Date(0)));
  const [cadTo, setCadTo] = useState<Date>(toStr ? endOfDay(new Date(toStr)) : endOfDay(new Date()));
  useEffect(() => {
    if (isEmbedded) {
      const handler = (e: MessageEvent) => {
        const data = (e.data || {}) as any;
        if (data && data.type === 'rx-period-change') {
          const nextFrom = data.range?.[0] ?? data.from;
          const nextTo = data.range?.[1] ?? data.to;
          if (nextFrom) setCadFrom(startOfDay(new Date(nextFrom)));
          if (nextTo) setCadTo(endOfDay(new Date(nextTo)));
          queryClient.invalidateQueries({ queryKey: ["product-transaction-items", id] });
          queryClient.invalidateQueries({ queryKey: ["product-stats", id] });
        }
      };
      window.addEventListener('message', handler);
      return () => window.removeEventListener('message', handler);
    }
  }, [isEmbedded]);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["product-transaction-items", id, cadFrom.toISOString(), cadTo.toISOString()] });
    queryClient.invalidateQueries({ queryKey: ["product-stats", id] });
  }, [cadFrom, cadTo]);

  useEffect(() => {
    queryClient.refetchQueries({ queryKey: ["product-transaction-items", id, cadFrom.toISOString(), cadTo.toISOString()] });
    queryClient.refetchQueries({ queryKey: ["product-stats", id] });
  }, [cadFrom, cadTo]);

  const { data: product, isLoading: productLoading } = useQuery({
    queryKey: ["product", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("id", id)
        .single();
      if (error) throw error;
      return data as Product;
    },
    enabled: !!id,
  });

  const { data: transactionItems, isLoading: itemsLoading } = useQuery({
    queryKey: ["product-transaction-items", id, cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transaction_items")
        .select(`
          *,
          transactions!inner (
            id,
            tipo,
            valor_total,
            valor_liquido,
            status,
            created_at,
            client_id,
            clients (id, nome)
          )
        `)
        .eq("product_id", id)
        .gte("transactions.created_at", cadFrom.toISOString())
        .lte("transactions.created_at", cadTo.toISOString())
        .order("created_at", { foreignTable: "transactions", ascending: false });
      if (error) throw error;
      return data as TransactionItem[];
    },
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
  });

  useEffect(() => {
    const loading = productLoading || itemsLoading;
    if (isEmbedded) {
      window.parent?.postMessage({ type: 'rx-loading', loading }, '*');
    }
  }, [productLoading, itemsLoading, isEmbedded]);

  // Buscar unidades de medida
  const { data: unitMeasurements = [] } = useQuery({
    queryKey: ["unit_measurements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("unit_measurements" as any)
        .select("*")
        .order("nome", { ascending: true });
      if (error) throw error;
      return (data || []) as any;
    },
  });

  const { data: stats } = useQuery({
    queryKey: ["product-stats", id, cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      if (!transactionItems || !product) return null;

      const sales = transactionItems.filter(item => (item as any).transactions?.tipo === "entrada");
      
      const totalRevenue = sales.reduce((sum, item) => sum + Number(item.total), 0);
      const totalQuantity = sales.reduce((sum, item) => sum + item.quantidade, 0);
      const totalTransactions = new Set(sales.map(item => item.transaction_id)).size;
      const averagePrice = totalQuantity > 0 ? totalRevenue / totalQuantity : 0;
      
      const totalCost = product.preco_custo 
        ? sales.reduce((sum, item) => sum + (Number(product.preco_custo) * item.quantidade), 0)
        : 0;
      const totalProfit = totalRevenue - totalCost;
      const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
      
      const lastSale = sales.length > 0 ? sales[0].transactions?.created_at : null;

      // Top clientes
      const clientMap = new Map<string, { quantity: number; revenue: number }>();
      sales.forEach(item => {
        const clientName = item.transactions?.clients?.nome || "Cliente não identificado";
        const current = clientMap.get(clientName) || { quantity: 0, revenue: 0 };
        clientMap.set(clientName, {
          quantity: current.quantity + item.quantidade,
          revenue: current.revenue + Number(item.total),
        });
      });
      const topClients = Array.from(clientMap.entries())
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

      // Vendas mensais (últimos 6 meses)
      const monthlyMap = new Map<string, { revenue: number; quantity: number }>();
      sales.forEach(item => {
        const month = format(new Date(item.transactions.created_at), "MMM/yy", { locale: ptBR });
        const current = monthlyMap.get(month) || { revenue: 0, quantity: 0 };
        monthlyMap.set(month, {
          revenue: current.revenue + Number(item.total),
          quantity: current.quantity + item.quantidade,
        });
      });
      const monthlySales = Array.from(monthlyMap.entries())
        .map(([month, data]) => ({ month, ...data }))
        .slice(-6);

      // Métodos de pagamento
      const transactionIds = [...new Set(sales.map(item => item.transaction_id))];
      let paymentMethods: { method: string; value: number }[] = [];
      if (transactionIds.length > 0 && tenantId) {
        const { data: pays } = await supabase
          .from('transaction_payments')
          .select('payment_method_id, valor, status, transaction_id')
          .in('transaction_id', transactionIds)
          .eq('status', 'confirmado')
          .eq('tenant_id', tenantId);

        const { data: pmRef } = await supabase
          .from('payment_methods')
          .select('id, nome');
        const nameById = new Map<string, string>();
        (pmRef || []).forEach((m: any) => nameById.set(m.id, m.nome));

        const paymentMap = new Map<string, number>();
        (pays || []).forEach((p: any) => {
          const id = p?.payment_method_id as string | null;
          const method = id ? (nameById.get(id) || id) : 'Não especificado';
          const valor = Number(p?.valor || 0);
          paymentMap.set(method, (paymentMap.get(method) || 0) + valor);
        });
        paymentMethods = Array.from(paymentMap.entries()).map(([method, value]) => ({ method, value }));
      }

      // Histórico de preços (simulado com base nas transações)
      const priceHistory = sales
        .map(item => ({
          date: format(new Date(item.transactions.created_at), "dd/MM/yyyy"),
          price: Number(item.preco_unitario),
        }))
        .slice(0, 20)
        .reverse();

      return {
        totalRevenue,
        totalQuantity,
        totalTransactions,
        averagePrice,
        profitMargin,
        totalProfit,
        lastSale,
        topClients,
        monthlySales,
        paymentMethods,
        priceHistory,
      } as ProductStats;
    },
    enabled: !!transactionItems && !!product,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
  });

  const updateMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const { error } = await supabase
        .from("products")
        .update({
          nome: formData.get("nome") as string,
          codigo_sku: (formData.get("codigo_sku") as string) || null,
          descricao: (formData.get("descricao") as string) || null,
          preco_venda: parseFloat(formData.get("preco_venda") as string),
          preco_custo: formData.get("preco_custo") ? parseFloat(formData.get("preco_custo") as string) : null,
          status: (formData.get("status") as string) || "ativo",
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product", id] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Produto atualizado com sucesso");
      if (id) {
        void logCrud({ entitySlug: "entity:products", operationSlug: "update", entityId: id, screenSlug: "produto" });
      }
      setIsEditing(false);
    },
    onError: () => toast.error("Erro ao atualizar produto"),
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    updateMutation.mutate(formData);
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);

  if (productLoading) {
    return <div className="p-8">Carregando...</div>;
  }

  if (!product) {
    return <div className="p-8">Produto não encontrado</div>;
  }

  const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))', '#8884d8', '#82ca9d'];

  return (
    <div className="space-y-6">
      {/* Header */}
      {!isEmbedded && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/produtos")}> 
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-4">
              {product.foto_url && (
                <img
                  src={product.foto_url}
                  alt={product.nome}
                  className="w-16 h-16 object-cover rounded-lg border"
                />
              )}
              <div>
                <h1 className="text-3xl font-bold">{product.nome}</h1>
                <p className="text-muted-foreground">
                  {product.codigo_sku && `SKU: ${product.codigo_sku} • `}
                  Produto desde {format(new Date(product.created_at), "dd/MM/yyyy")}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPIs Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Receita Total</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? formatCurrency((stats as any).totalRevenue) : "-"}
            </div>
            <p className="text-xs text-muted-foreground">
              {(stats as any)?.totalTransactions || 0} transações
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Quantidade Vendida</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.totalQuantity || 0}
            </div>
            <p className="text-xs text-muted-foreground">unidades</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Margem de Lucro</CardTitle>
            <Percent className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? `${stats.profitMargin.toFixed(1)}%` : "-"}
            </div>
            <p className="text-xs text-muted-foreground">
              Lucro: {stats ? formatCurrency(stats.totalProfit) : "-"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Última Venda</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.lastSale 
                ? format(new Date(stats.lastSale), "dd/MM/yyyy")
                : "Nunca"}
            </div>
            <p className="text-xs text-muted-foreground">
              Preço médio: {stats ? formatCurrency(stats.averagePrice) : "-"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs Content */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="sales">Vendas</TabsTrigger>
          <TabsTrigger value="data">Dados do Produto</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        {/* Visão Geral */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Informações do Produto */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Informações do Produto
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Preço de Venda</span>
                  <span className="text-lg font-bold">{formatCurrency(product.preco_venda)}</span>
                </div>
                {product.preco_custo && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Preço de Custo</span>
                    <span className="text-lg">{formatCurrency(product.preco_custo)}</span>
                  </div>
                )}
                {product.preco_custo && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Margem</span>
                    <span className="text-lg font-semibold text-green-600">
                      {((product.preco_venda - product.preco_custo) / product.preco_venda * 100).toFixed(1)}%
                    </span>
                  </div>
                )}
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Status</span>
                  <Badge className="bg-blue-600 text-white">
                    {product.status}
                  </Badge>
                </div>
                {product.codigo_sku && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">SKU</span>
                    <span className="text-sm font-mono">{product.codigo_sku}</span>
                  </div>
                )}
                {product.descricao && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-sm font-medium mb-2">Descrição:</p>
                      <p className="text-sm text-muted-foreground">{product.descricao}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Top Clientes */}
            <Card>
              <CardHeader>
                <CardTitle>Top 5 Clientes</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.topClients && stats.topClients.length > 0 ? (
                  <div className="space-y-3">
                    {stats.topClients.map((client, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{client.name}</p>
                          <p className="text-sm text-muted-foreground">{client.quantity}x unidades</p>
                        </div>
                        <Badge className="bg-blue-600 text-white">
                          {formatCurrency(client.revenue)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">Nenhuma venda registrada</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Gráficos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Vendas Mensais */}
            <Card>
              <CardHeader>
                <CardTitle>Evolução de Vendas</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.monthlySales && stats.monthlySales.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.monthlySales}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="hsl(var(--primary))" 
                        strokeWidth={2}
                        name="Receita (R$)"
                      />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="quantity" 
                        stroke="hsl(var(--accent))" 
                        strokeWidth={2}
                        name={(() => {
                          const saleUnitId = product?.unit_measurement_sale_id;
                          const saleUnit = saleUnitId ? unitMeasurements?.find(u => u.id === saleUnitId) : null;
                          return saleUnit ? `Quantidade (${saleUnit.nome})` : "Quantidade";
                        })()}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados de vendas</p>
                )}
              </CardContent>
            </Card>

            {/* Métodos de Pagamento */}
            <Card>
              <CardHeader>
                <CardTitle>Métodos de Pagamento</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.paymentMethods && stats.paymentMethods.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={stats.paymentMethods}
                        dataKey="value"
                        nameKey="method"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={(entry: any) => formatCurrency(entry.value)}
                      >
                        {stats.paymentMethods.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value: number) => formatCurrency(value)}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados de pagamento</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Vendas */}
        <TabsContent value="sales" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Vendas</CardTitle>
            </CardHeader>
            <CardContent>
              {itemsLoading ? (
                <p className="text-center py-8">Carregando...</p>
              ) : transactionItems && transactionItems.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Quantidade</TableHead>
                      <TableHead>Preço Unitário</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactionItems
                      .filter(item => item.transactions?.tipo === "entrada")
                      .map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            {format(new Date(item.created_at), "dd/MM/yyyy HH:mm")}
                          </TableCell>
                          <TableCell>
                            {item.transactions?.clients?.nome || "Cliente não identificado"}
                          </TableCell>
                          <TableCell>{item.quantidade}</TableCell>
                          <TableCell>{formatCurrency(item.preco_unitario)}</TableCell>
                          <TableCell>
                            <Badge variant={item.transactions?.status === "confirmada" ? "default" : "outline"}>
                              {item.transactions?.status || "N/A"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(item.total)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground text-center py-8">Nenhuma venda registrada</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dados do Produto */}
        <TabsContent value="data" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Dados do Produto</CardTitle>
              {!isEditing && (
                <Button variant="outline" onClick={() => setIsEditing(true)}>
                  Editar
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nome">Nome *</Label>
                      <Input id="nome" name="nome" defaultValue={product.nome} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="codigo_sku">Código/SKU</Label>
                      <Input id="codigo_sku" name="codigo_sku" defaultValue={product.codigo_sku || ""} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="preco_venda">Preço de Venda *</Label>
                      <Input 
                        id="preco_venda" 
                        name="preco_venda" 
                        type="number" 
                        step="0.01" 
                        defaultValue={product.preco_venda} 
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="preco_custo">Preço de Custo</Label>
                      <Input 
                        id="preco_custo" 
                        name="preco_custo" 
                        type="number" 
                        step="0.01" 
                        defaultValue={product.preco_custo || ""} 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <select
                        id="status"
                        name="status"
                        defaultValue={product.status}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                      >
                        <option value="ativo">Ativo</option>
                        <option value="inativo">Inativo</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="descricao">Descrição</Label>
                    <Textarea 
                      id="descricao" 
                      name="descricao" 
                      defaultValue={product.descricao || ""} 
                      rows={3} 
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit">
                      <Save className="mr-2 h-4 w-4" />
                      Salvar Alterações
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setIsEditing(false)}>
                      Cancelar
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Nome</p>
                      <p>{product.nome}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">SKU</p>
                      <p>{product.codigo_sku || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Preço de Venda</p>
                      <p className="text-lg font-bold">{formatCurrency(product.preco_venda)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Preço de Custo</p>
                      <p>{product.preco_custo ? formatCurrency(product.preco_custo) : "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Status</p>
                      <Badge variant={product.status === "ativo" ? "default" : "secondary"}>
                        {product.status}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Criado em</p>
                      <p>{format(new Date(product.created_at), "dd/MM/yyyy HH:mm")}</p>
                    </div>
                  </div>
                  {product.descricao && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Descrição</p>
                      <p>{product.descricao}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline */}
        <TabsContent value="timeline" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Timeline de Vendas</CardTitle>
            </CardHeader>
            <CardContent>
              {transactionItems && transactionItems.filter(item => item.transactions?.tipo === "entrada").length > 0 ? (
                <div className="relative border-l-2 border-muted pl-6 space-y-6">
                  {transactionItems
                    .filter(item => item.transactions?.tipo === "entrada")
                    .map((item) => (
                      <div key={item.id} className="relative">
                        <div className="absolute -left-[29px] w-4 h-4 rounded-full bg-primary border-4 border-background" />
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(item.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                          </p>
                          <p className="font-medium">
                            Venda de {item.quantidade} unidade(s)
                          </p>
                          <div className="text-sm">
                            <p>Cliente: {item.transactions?.clients?.nome || "Não identificado"}</p>
                            <p>Valor: {formatCurrency(item.total)}</p>
                            <p>Status: {item.transactions?.status}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">Nenhuma venda registrada</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics */}
        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {stats?.totalTransactions || 0}
                </div>
                <p className="text-sm text-muted-foreground">Total de transações</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Rentabilidade</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {stats ? formatCurrency(stats.totalProfit) : "R$ 0,00"}
                </div>
                <p className="text-sm text-muted-foreground">Lucro total acumulado</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Média de Vendas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {stats?.totalTransactions 
                    ? (stats.totalQuantity / stats.totalTransactions).toFixed(1)
                    : 0}
                </div>
                <p className="text-sm text-muted-foreground">Unidades por transação</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Top Clientes por Quantidade</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.topClients && stats.topClients.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.topClients}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="quantity" fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-center py-12">Sem dados disponíveis</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

